import { useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase/config";
import { C, F, Label, Btn, Spinner } from "../ui";
import { fmtUSD, calcDDP } from "../../utils/ddp";

// ── Genera el HTML de la proforma ─────────────────────────────────────────────
function generarProformaHTML(pedido, globales) {
  const fecha = new Date().toLocaleDateString("es-AR", { day:"2-digit", month:"long", year:"numeric" });
  const items = pedido.items || [];
  const tc = globales?.tipo_cambio || 1050;

  const filasItems = items.map(item => {
    const subtotal = (item.fob_venta || 0) * item.qty;
    return `
      <tr>
        <td style="padding:10px 12px;border-bottom:0.5px solid #E5E5E5;font-size:12px;">${item.nombre}</td>
        <td style="padding:10px 12px;border-bottom:0.5px solid #E5E5E5;font-size:12px;text-align:center;">${item.sku}</td>
        <td style="padding:10px 12px;border-bottom:0.5px solid #E5E5E5;font-size:12px;text-align:center;">${item.qty} ${item.unidad}</td>
        <td style="padding:10px 12px;border-bottom:0.5px solid #E5E5E5;font-size:12px;text-align:right;font-family:monospace;">USD ${(item.fob_venta||0).toFixed(2)}</td>
        <td style="padding:10px 12px;border-bottom:0.5px solid #E5E5E5;font-size:12px;text-align:right;font-family:monospace;">USD ${subtotal.toFixed(2)}</td>
      </tr>`;
  }).join("");

  const ddp = calcDDP(pedido.fob_total, pedido.cbm_total, 1, { ...globales, cbm_max: 68 });

  return `<!DOCTYPE html>
<html lang="es">
<head><meta charset="UTF-8"/>
<style>
  body{font-family:'Helvetica Neue',Arial,sans-serif;margin:0;padding:40px;color:#111;background:#fff;font-size:13px;}
  h1{font-family:Georgia,serif;font-size:28px;font-weight:400;letter-spacing:3px;margin:0;}
  .header{display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:24px;border-bottom:2px solid #111;margin-bottom:28px;}
  .label{font-size:9px;letter-spacing:2px;text-transform:uppercase;color:#888;margin-bottom:4px;}
  table{width:100%;border-collapse:collapse;margin:16px 0;}
  th{font-size:9px;letter-spacing:2px;text-transform:uppercase;color:#888;padding:8px 12px;text-align:left;border-bottom:1px solid #111;}
  .total-row td{padding:10px 12px;font-weight:600;}
  .footer{margin-top:40px;padding-top:20px;border-top:0.5px solid #ddd;font-size:10px;color:#aaa;}
</style>
</head>
<body>
<div class="header">
  <div>
    <h1>ORIGEN</h1>
    <div style="font-size:10px;letter-spacing:2px;color:#888;margin-top:4px;">IMPORT REAL ESTATE</div>
  </div>
  <div style="text-align:right;">
    <div class="label">Proforma</div>
    <div style="font-size:20px;font-family:monospace;">#${pedido.id?.slice(-6).toUpperCase() || "000001"}</div>
    <div style="font-size:11px;color:#888;margin-top:4px;">${fecha}</div>
  </div>
</div>

<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px;margin-bottom:28px;">
  <div>
    <div class="label">Cliente</div>
    <div style="font-size:13px;font-weight:500;">${pedido.cliente_nombre || "—"}</div>
    <div style="font-size:11px;color:#666;">${pedido.cliente_email || ""}</div>
  </div>
  <div>
    <div class="label">Ciclo / Contenedor</div>
    <div style="font-size:13px;">${pedido.ciclo_nombre || "—"}</div>
  </div>
  <div>
    <div class="label">Tipo de cambio</div>
    <div style="font-size:13px;font-family:monospace;">ARS ${tc.toLocaleString("es-AR")}</div>
    <div style="font-size:10px;color:#888;">Dólar BNA al ${fecha}</div>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th>Producto</th><th>SKU</th><th style="text-align:center;">Cantidad</th>
      <th style="text-align:right;">Precio Unit.</th><th style="text-align:right;">Subtotal</th>
    </tr>
  </thead>
  <tbody>${filasItems}</tbody>
</table>

<div style="display:flex;justify-content:flex-end;margin-top:8px;">
  <div style="width:300px;">
    <table style="margin:0;">
      <tbody>
        ${[
          ["FOB Base", `USD ${(pedido.fob_total||0).toFixed(2)}`],
          ["Flete Internacional (prop.)", `USD ${ddp.flete.toFixed(2)}`],
          ["Seguro", `USD ${ddp.seg.toFixed(2)}`],
          ["Aranceles", `USD ${ddp.aranc.toFixed(2)}`],
          ["IVA 21%", `USD ${ddp.iv1.toFixed(2)}`],
          ["IVA Adicional 20%", `USD ${ddp.iv2.toFixed(2)}`],
          ["IIBB 3%", `USD ${ddp.ib.toFixed(2)}`],
          ["Gastos de Nac.", `USD ${ddp.gnac.toFixed(2)}`],
        ].map(([l,v]) => `<tr><td style="padding:5px 12px;font-size:11px;color:#666;">${l}</td><td style="padding:5px 12px;font-size:11px;text-align:right;font-family:monospace;">${v}</td></tr>`).join("")}
        <tr style="border-top:2px solid #111;">
          <td style="padding:12px;font-size:13px;font-weight:600;">TOTAL DDP</td>
          <td style="padding:12px;font-size:15px;font-weight:700;text-align:right;font-family:monospace;">USD ${(pedido.ddp_total||0).toFixed(2)}</td>
        </tr>
        <tr>
          <td style="padding:4px 12px;font-size:10px;color:#888;">En pesos</td>
          <td style="padding:4px 12px;font-size:12px;text-align:right;font-family:monospace;color:#888;">ARS ${((pedido.ddp_total||0)*tc).toLocaleString("es-AR",{minimumFractionDigits:0})}</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<div class="footer">
  <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;">
    <div>
      <div class="label" style="margin-bottom:3px;">Depósito Buenos Aires</div>
      Batalla del Pari 1053, CABA<br/>Lunes a viernes 9 a 18 hs
    </div>
    <div>
      <div class="label" style="margin-bottom:3px;">Condiciones</div>
      Precio DDP puesto en depósito CABA.<br/>No incluye entrega a obra.
    </div>
    <div>
      <div class="label" style="margin-bottom:3px;">Validez</div>
      Esta proforma tiene validez de 15 días<br/>sujeto a tipo de cambio vigente.
    </div>
  </div>
</div>
</body></html>`;
}

// ── Genera el HTML de la Purchase Order ──────────────────────────────────────
function generarPOHTML(ciclo, itemsPorProveedor, globales) {
  const fecha = new Date().toLocaleDateString("es-AR", { day:"2-digit", month:"long", year:"numeric" });

  const secciones = Object.entries(itemsPorProveedor).map(([proveedor, data]) => {
    const filas = data.items.map(item => `
      <tr>
        <td style="padding:8px 10px;border-bottom:0.5px solid #eee;font-size:11px;">${item.nombre}</td>
        <td style="padding:8px 10px;border-bottom:0.5px solid #eee;font-size:11px;text-align:center;">${item.sku}</td>
        <td style="padding:8px 10px;border-bottom:0.5px solid #eee;font-size:11px;text-align:center;">${item.qty} ${item.unidad}</td>
        <td style="padding:8px 10px;border-bottom:0.5px solid #eee;font-size:11px;text-align:right;font-family:monospace;">USD ${(item.fob_compra||0).toFixed(2)}</td>
        <td style="padding:8px 10px;border-bottom:0.5px solid #eee;font-size:11px;text-align:right;font-family:monospace;">USD ${((item.fob_compra||0)*item.qty).toFixed(2)}</td>
      </tr>`).join("");

    return `
      <div style="margin-bottom:32px;page-break-inside:avoid;">
        <div style="background:#111;color:#fff;padding:10px 14px;margin-bottom:0;">
          <div style="font-size:10px;letter-spacing:2px;opacity:.6;text-transform:uppercase;">Proveedor</div>
          <div style="font-size:16px;font-family:Georgia,serif;">${proveedor}</div>
        </div>
        <table style="width:100%;border-collapse:collapse;margin:0;">
          <thead>
            <tr style="background:#f5f5f5;">
              <th style="padding:7px 10px;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#888;text-align:left;">Producto</th>
              <th style="padding:7px 10px;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#888;">SKU</th>
              <th style="padding:7px 10px;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#888;text-align:center;">Qty</th>
              <th style="padding:7px 10px;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#888;text-align:right;">FOB Unit.</th>
              <th style="padding:7px 10px;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#888;text-align:right;">Subtotal</th>
            </tr>
          </thead>
          <tbody>${filas}</tbody>
          <tfoot>
            <tr style="border-top:1px solid #111;">
              <td colspan="4" style="padding:10px;font-weight:600;font-size:12px;">Total ${proveedor}</td>
              <td style="padding:10px;font-weight:700;font-size:13px;text-align:right;font-family:monospace;">USD ${data.fob_total.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>`;
  }).join("");

  const totalPO = Object.values(itemsPorProveedor).reduce((s, d) => s + d.fob_total, 0);

  return `<!DOCTYPE html>
<html lang="es">
<head><meta charset="UTF-8"/>
<style>
  body{font-family:'Helvetica Neue',Arial,sans-serif;margin:0;padding:40px;color:#111;font-size:13px;}
  h1{font-family:Georgia,serif;font-size:28px;font-weight:400;letter-spacing:3px;margin:0;}
  .label{font-size:9px;letter-spacing:2px;text-transform:uppercase;color:#888;margin-bottom:4px;}
  @media print{.no-print{display:none}}
</style>
</head>
<body>
<div style="display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:24px;border-bottom:2px solid #111;margin-bottom:32px;">
  <div>
    <h1>ORIGEN</h1>
    <div style="font-size:10px;letter-spacing:2px;color:#888;margin-top:4px;">PURCHASE ORDER</div>
  </div>
  <div style="text-align:right;">
    <div class="label">Ciclo</div>
    <div style="font-size:18px;font-family:Georgia,serif;">${ciclo.nombre}</div>
    <div style="font-size:11px;color:#888;margin-top:4px;">${fecha}</div>
  </div>
</div>

<div style="display:grid;grid-template-columns:repeat(4,1fr);gap:20px;margin-bottom:32px;padding:16px;background:#f9f9f9;">
  <div><div class="label">Contenedor</div><div>${ciclo.tipo_contenedor || "40HC"}</div></div>
  <div><div class="label">Origen</div><div>${ciclo.origen || "Guangzhou"}</div></div>
  <div><div class="label">Zarpe estimado</div><div>${ciclo.fecha_zarpe || "—"}</div></div>
  <div><div class="label">Arribo BA</div><div>${ciclo.fecha_arribo || "—"}</div></div>
</div>

${secciones}

<div style="border-top:2px solid #111;padding-top:16px;display:flex;justify-content:flex-end;">
  <div style="text-align:right;">
    <div class="label">Total General Purchase Order</div>
    <div style="font-size:24px;font-family:monospace;font-weight:700;">USD ${totalPO.toFixed(2)}</div>
  </div>
</div>
</body></html>`;
}

// ── COMPONENTE PRINCIPAL ──────────────────────────────────────────────────────
export default function AdminDocumentos() {
  const [pedidos, setPedidos]   = useState([]);
  const [ciclos, setCiclos]     = useState([]);
  const [globales, setGlobales] = useState({});
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState("proformas");
  const [cicloPO, setCicloPO]   = useState("");
  const [generando, setGenerando] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [pedSnap, cicSnap, gSnap] = await Promise.all([
        getDocs(query(collection(db, "pedidos"), orderBy("creadoEn", "desc"))),
        getDocs(query(collection(db, "ciclos"),  orderBy("creadoEn", "desc"))),
        getDoc(doc(db, "configuracion", "globales")),
      ]);
      setPedidos(pedSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCiclos(cicSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setGlobales(gSnap.exists() ? gSnap.data() : {});
      if (cicSnap.docs.length > 0) setCicloPO(cicSnap.docs[0].id);
      setLoading(false);
    };
    load();
  }, []);

  const abrirProforma = (pedido) => {
    const html = generarProformaHTML(pedido, globales);
    const ventana = window.open("", "_blank");
    ventana.document.write(html);
    ventana.document.close();
    setTimeout(() => ventana.print(), 500);
  };

  const abrirPO = () => {
    const ciclo = ciclos.find(c => c.id === cicloPO);
    if (!ciclo) return;
    setGenerando(true);

    // Construir mapa de items por proveedor desde los pedidos del ciclo
    const pedidosCiclo = pedidos.filter(p => p.ciclo_id === cicloPO);
    const porProveedor = {};

    pedidosCiclo.forEach(p => {
      (p.items || []).forEach(item => {
        const prov = item.proveedor || "Sin proveedor asignado";
        if (!porProveedor[prov]) porProveedor[prov] = { items: [], fob_total: 0 };

        // Verificar si ya existe este SKU
        const existing = porProveedor[prov].items.find(x => x.sku === item.sku);
        if (existing) {
          existing.qty += item.qty;
          porProveedor[prov].fob_total += (item.fob_compra || item.fob_venta * 0.5) * item.qty;
        } else {
          porProveedor[prov].items.push({ ...item, fob_compra: item.fob_compra || item.fob_venta * 0.5 });
          porProveedor[prov].fob_total += (item.fob_compra || item.fob_venta * 0.5) * item.qty;
        }
      });
    });

    const html = generarPOHTML(ciclo, porProveedor, globales);
    const ventana = window.open("", "_blank");
    ventana.document.write(html);
    ventana.document.close();
    setTimeout(() => { ventana.print(); setGenerando(false); }, 500);
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: F.serif, fontSize: 24, color: C.white, marginBottom: 4 }}>Documentos</div>
        <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>Proformas para clientes y Purchase Orders para proveedores</div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: `0.5px solid ${C.border}`, marginBottom: 24 }}>
        {[["proformas","Proformas"],["po","Purchase Orders"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: "10px 20px", background: "transparent", border: "none",
            borderBottom: tab===k ? `1px solid ${C.white}` : "1px solid transparent",
            color: tab===k ? C.white : C.muted,
            fontFamily: F.sans, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
          }}>{l}</button>
        ))}
      </div>

      {tab === "proformas" && (
        <div>
          <div style={{ fontFamily: F.sans, fontSize: 12, color: C.muted, marginBottom: 16, lineHeight: 1.6 }}>
            Generá la proforma de cualquier pedido. Se abre en una nueva ventana lista para imprimir o guardar como PDF.
          </div>
          <div style={{ background: C.border }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 100px 100px 120px", background: C.s2, padding: "7px 14px", gap: 8 }}>
              {["Cliente","Ciclo","FOB","DDP",""].map(h => (
                <div key={h} style={{ fontFamily: F.sans, fontSize: 8, color: C.muted, letterSpacing: 2, textTransform: "uppercase" }}>{h}</div>
              ))}
            </div>
            {pedidos.length === 0 && (
              <div style={{ background: C.s1, padding: 32, fontFamily: F.sans, fontSize: 12, color: C.muted, textAlign: "center" }}>
                Sin pedidos. Cuando los compradores confirmen pedidos aparecerán acá.
              </div>
            )}
            {pedidos.map(p => (
              <div key={p.id} style={{ display: "grid", gridTemplateColumns: "1fr 140px 100px 100px 120px", background: C.s1, padding: "12px 14px", gap: 8, borderBottom: `0.5px solid ${C.border}`, alignItems: "center" }}>
                <div>
                  <div style={{ fontFamily: F.sans, fontSize: 12, color: C.white }}>{p.cliente_nombre || p.cliente_email || "—"}</div>
                  <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>{p.items?.length || 0} ítems</div>
                </div>
                <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>{p.ciclo_nombre || "—"}</div>
                <div style={{ fontFamily: F.mono, fontSize: 11, color: C.off }}>{fmtUSD(p.fob_total)}</div>
                <div style={{ fontFamily: F.mono, fontSize: 11, color: C.white }}>{fmtUSD(p.ddp_total)}</div>
                <Btn variant="ghost" size="sm" onClick={() => abrirProforma(p)}>
                  Generar →
                </Btn>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "po" && (
        <div>
          <div style={{ fontFamily: F.sans, fontSize: 12, color: C.muted, marginBottom: 24, lineHeight: 1.6 }}>
            La Purchase Order consolida todos los pedidos del ciclo, agrupa por proveedor y muestra el FOB de compra.
            Se genera lista para imprimir o enviar a las fábricas en China.
          </div>

          <div style={{ background: C.s1, border: `0.5px solid ${C.border}`, padding: 28, maxWidth: 480 }}>
            <Label style={{ marginBottom: 6 }}>Seleccionar ciclo</Label>
            <select value={cicloPO} onChange={e => setCicloPO(e.target.value)}
              style={{ width: "100%", background: C.s2, border: `0.5px solid ${C.border2}`, color: C.off, fontFamily: F.sans, fontSize: 12, padding: "8px 10px", outline: "none", marginBottom: 20 }}>
              {ciclos.map(c => (
                <option key={c.id} value={c.id}>{c.nombre} — {c.estado}</option>
              ))}
            </select>

            {cicloPO && (() => {
              const pedidosCiclo = pedidos.filter(p => p.ciclo_id === cicloPO);
              return (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    {[
                      ["Pedidos", pedidosCiclo.length],
                      ["FOB Total", fmtUSD(pedidosCiclo.reduce((s,p)=>s+(p.fob_total||0),0))],
                      ["CBM", `${pedidosCiclo.reduce((s,p)=>s+(p.cbm_total||0),0).toFixed(2)} m³`],
                    ].map(([l,v]) => (
                      <div key={l} style={{ padding: "10px 12px", background: C.s2 }}>
                        <Label style={{ marginBottom: 4 }}>{l}</Label>
                        <div style={{ fontFamily: F.mono, fontSize: 13, color: C.white }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <Btn variant="solid" onClick={abrirPO} disabled={!cicloPO || generando}
              style={{ width: "100%", justifyContent: "center" }}>
              {generando ? "Generando..." : "Generar Purchase Order →"}
            </Btn>

            <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted, marginTop: 10, textAlign: "center" }}>
              Se abre en nueva ventana · Lista para imprimir o PDF
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
